import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  buildFileRecordKind,
  ObjectActionCardMenu,
  useCanvasObjectActionHandlers,
  useObjectActions,
  useResolvedFileRecordActions,
  type ObjectActionHandlers,
  type ObjectKind,
} from "@/object-actions";
import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import { useAttachCanvasTransferSubmenu } from "@/object-actions/transfer/useCanvasTransferSubmenu";
import {
  RESOURCE_TAG_FAVORITE,
  type Canvas,
  type FileRecord,
  type InboxItem,
} from "@cohesive/api-client";
import React, { useMemo } from "react";
import type { BrowserItem } from "../utils/homeBrowserTypes";
import { browserItemName } from "../utils/homeSort";
import { HomeBrowserListRow } from "./HomeBrowserListRow";

/**
 * Registry-backed list rows — the exact counterparts of the grid's
 * `CanvasCardContainer` / `WorkspaceFileCardItem` / `ChatCardContainer`.
 * Each owns one `useRailRowContextMenu` controller so the row's hover kebab
 * and right-click open the same manifest-driven menu as the grid card,
 * keeping the two views' action sets structurally identical by
 * construction (they resolve from the same object-actions registry).
 */

const CANVAS_KIND: ObjectKind = { type: "canvas" };
const CHAT_KIND: ObjectKind = { type: "chat" };

/** Selection wiring forwarded verbatim to `HomeBrowserListRow`. */
interface RowSelectionPassthrough {
  selectionId?: string;
  isSelected?: boolean;
  onSelect?: (e: React.MouseEvent) => void;
}

export function CanvasBrowserListRow({
  item,
  canvas,
  sharedWithMe,
  canShare,
  canDelete,
  onOpen,
  ...selectionProps
}: {
  item: BrowserItem;
  canvas: Canvas;
  sharedWithMe?: boolean;
  canShare: boolean;
  canDelete: boolean;
  onOpen: () => void;
} & RowSelectionPassthrough) {
  const controller = useRailRowContextMenu();
  const isFavorite = !!canvas.tags?.includes(RESOURCE_TAG_FAVORITE);
  const handlers = useCanvasObjectActionHandlers({
    canvas,
    canShare,
    canDelete,
  });
  const { actions: baseActions } = useObjectActions({
    kind: CANVAS_KIND,
    surface: "context-menu",
    enabled: controller.open,
    state: {
      isFavorite,
      canShare,
      canDelete,
      canEdit: canvas.can_edit !== false,
    },
    handlers,
  });
  // Move to opens the nested destination picker, same as the rail row.
  const actions = useAttachCanvasTransferSubmenu(baseActions, canvas);

  return (
    <HomeBrowserListRow
      item={item}
      sharedWithMe={sharedWithMe}
      onOpen={onOpen}
      onContextMenu={controller.onContextMenu}
      menuOpen={controller.open}
      menuSlot={
        <ObjectActionCardMenu
          controller={controller}
          actions={actions}
          kind={CANVAS_KIND}
          name={canvas.name || "Untitled"}
        />
      }
      {...selectionProps}
    />
  );
}

export function FileBrowserListRow({
  item,
  record,
  sharedWithMe,
  onOpen,
  handlerDeps,
  ...selectionProps
}: {
  item: BrowserItem;
  record: FileRecord;
  sharedWithMe?: boolean;
  onOpen: () => void;
  handlerDeps: FileRecordActionHandlerDeps;
} & RowSelectionPassthrough) {
  const controller = useRailRowContextMenu();
  const kind = useMemo(() => buildFileRecordKind(record), [record]);
  const { actions } = useResolvedFileRecordActions({
    record,
    surface: "main-view",
    enabled: controller.open,
    handlerDeps,
  });

  return (
    <HomeBrowserListRow
      item={item}
      sharedWithMe={sharedWithMe}
      onOpen={onOpen}
      onContextMenu={controller.onContextMenu}
      menuOpen={controller.open}
      menuSlot={
        <ObjectActionCardMenu
          controller={controller}
          actions={actions}
          kind={kind}
          name={browserItemName(item)}
        />
      }
      {...selectionProps}
    />
  );
}

export function ChatBrowserListRow({
  item,
  chat,
  sharedWithMe,
  onOpen,
  onDelete,
  onToggleStar,
  onRename,
  ...selectionProps
}: {
  item: BrowserItem;
  chat: InboxItem;
  sharedWithMe?: boolean;
  onOpen: (chat: InboxItem) => void;
  onDelete: (chat: InboxItem) => void;
  onToggleStar: (chat: InboxItem) => void;
  onRename: (chat: InboxItem) => void;
} & RowSelectionPassthrough) {
  const controller = useRailRowContextMenu();
  const handlers = useMemo<ObjectActionHandlers>(
    () => ({
      open: () => onOpen(chat),
      rename: () => onRename(chat),
      delete: () => onDelete(chat),
      star: () => onToggleStar(chat),
      unstar: () => onToggleStar(chat),
    }),
    [chat, onOpen, onRename, onDelete, onToggleStar],
  );
  const { actions } = useObjectActions({
    kind: CHAT_KIND,
    surface: "context-menu",
    enabled: controller.open,
    state: { isFavorite: !!chat.is_starred },
    handlers,
  });

  return (
    <HomeBrowserListRow
      item={item}
      sharedWithMe={sharedWithMe}
      onOpen={() => onOpen(chat)}
      onContextMenu={controller.onContextMenu}
      menuOpen={controller.open}
      menuSlot={
        <ObjectActionCardMenu
          controller={controller}
          actions={actions}
          kind={CHAT_KIND}
          name={chat.flow_session?.name || "Untitled chat"}
        />
      }
      {...selectionProps}
    />
  );
}
